import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Google Analytics 4 + Google Tag Manager integration.
 *
 * SETUP:
 * 1. Get your GA4 Measurement ID from analytics.google.com  (looks like "G-XXXXXXXXXX")
 * 2. (Optional) Get your GTM Container ID from tagmanager.google.com  ("GTM-XXXXXXX")
 * 3. Paste them below, save, and analytics will start tracking every page view —
 *    including each suburb landing page individually.
 *
 * Form submissions automatically fire as `lead_submit` events.
 */
const GA_MEASUREMENT_ID = ""; // e.g. "G-1A2B3C4D5E"
const GTM_CONTAINER_ID = ""; // e.g. "GTM-XYZ1234"

let analyticsLoaded = false;

const loadAnalytics = () => {
  if (analyticsLoaded || typeof window === "undefined") return;
  if (!GA_MEASUREMENT_ID && !GTM_CONTAINER_ID) return;
  analyticsLoaded = true;

  // GA4 (gtag.js)
  if (GA_MEASUREMENT_ID) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", GA_MEASUREMENT_ID, {
      send_page_view: false, // we control this manually on route change
    });
  }

  // GTM
  if (GTM_CONTAINER_ID) {
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s);
      const dl = l !== "dataLayer" ? `&l=${l}` : "";
      j.async = true;
      j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
      f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", GTM_CONTAINER_ID);
  }
};

const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = location.pathname + location.search;

    if (window.gtag && GA_MEASUREMENT_ID) {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: document.title,
        page_location: window.location.href,
      });
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: "page_view",
        page_path: path,
        page_title: document.title,
      });
    }
  }, [location]);

  return null;
};

/**
 * Helper to track conversions (forms, calls, etc) from anywhere in the app.
 *
 * Example:
 *   trackEvent("lead_submit", { source: "hero_form", suburb: "Ascot" });
 */
export const trackEvent = (name, params = {}) => {
  if (typeof window === "undefined") return;
  if (window.gtag) window.gtag("event", name, params);
  if (window.dataLayer) {
    window.dataLayer.push({ event: name, ...params });
  }
};

export default Analytics;
